import "./SlideViewer.scss";

import "regenerator-runtime/runtime";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage, usePrevious } from "react-use";

import { Case, Slide, SlideClassification, Specimen } from "pwapi";

import {
  fetchCaseDiagnoses,
  openDocument,
} from "shared/actions/caseDetailActions";
import { useViewableSlideStatuses } from "shared/hooks/slideHooks";
import { useCanSeeFrontEndAdmin } from "shared/hooks/usePermissions";
import { AuthState } from "shared/reducers/authReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import { parseHash } from "shared/utils/openseadragon/bookmark_url";

import AppSpinner from "AppSpinner";
import AppWarning from "AppWarning";
import LinkButton from "components/LinkButton";
import useSiteSpecificCodes from "hooks/useSiteSpecificCodes";
import useViewerItems from "hooks/useViewerItems";
import ApplicationState from "store/ApplicationState";

import AIOverlayDialog from "./dialogs/AIOverlayDialog";
import DiagnosisDialogController, {
  isInputDiagnosis,
} from "./dialogs/DiagnosisDialogController";
import { DialogEnum } from "./dialogs/Dialog";
import HelpDialog from "./dialogs/HelpDialog";
import { shouldShowSpecimenDialog } from "./dialogs/SpecimenDialog";
import SpecimenDialogController from "./dialogs/SpecimenDialogController";
import StainOrderDialog from "./dialogs/StainOrderDialog";
import { canSignoutCase, slideClassification } from "./dialogs/utils";
import { OldItem } from "./DocsGrid";
import FlagList from "./FlagList";
import AnnotationModal from "./modals/AnnotationModal";
import ErrorModal from "./modals/ErrorModal";
import PatientModal from "./modals/PatientModal";
import PhysicianPreferencesModal from "./modals/PhysicianPreferencesModal";
import WorkflowModal from "./modals/WorkflowModal";
import SideWindow from "./SideWindow";
import SlideButtons from "./SlideButtons";
import SlideComponent from "./SlideComponent";
import SlideHeader from "./SlideHeader";
import SlideHeaderCaseless from "./SlideHeaderCaseless";
import SlideThumbnailWidget from "./SlideThumbnailWidget";

interface SlideViewerProps {
  authObject: AuthState;
  caseDetailObject?: CaseDetailState;
  slideDetailObject: SlideDetailState;
  aiObject?: SlideClassification;
  hasOrderedDocuments?: boolean;
  documentId?: number;
  specimenId?: number;
  slideId?: number;
  sourceQuery?: string | null;
}

const SlideViewer = (props: SlideViewerProps) => {
  const {
    authObject,
    slideDetailObject,
    caseDetailObject,
    aiObject,
    documentId,
    specimenId,
    slideId,
    sourceQuery,
  } = props;

  const isAvifSlide = slideDetailObject?.low_power_dzi?.format === "avif";

  const dispatch = useDispatch();
  const canSeeAdmin = useCanSeeFrontEndAdmin(["dermpath"]);

  // Calculate these here:
  let archived =
    !!slideDetailObject?.id && !slideDetailObject.available_in_storage;
  let amended = false;
  // let completed = false; not used yet

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  const avifSupported: boolean | undefined = useSelector(
    ({ sys }: ApplicationState) => sys.browser?.avifSupported
  );

  const user = useSelector(({ auth }: ApplicationState) => auth.user);
  const hasImpersonator: boolean = useSelector(
    ({ auth }: ApplicationState) => !!auth.access?.impersonator_id
  );

  const caseLogExists =
    props.caseDetailObject?.case_log &&
    props.caseDetailObject?.case_log.length > 0;
  const slideAnnotationsExist =
    (props.slideDetailObject.annotations &&
      props.slideDetailObject.annotations.length > 0) ||
    props.slideDetailObject.pendingAnnotationsBuffer.length > 0;
  const classificationInfo = slideClassification(slideDetailObject);

  // For storing the slide ID when dialog is set to InputDiagnosis
  const [
    slideIdWithInputDiagnosisOpen,
    setSlideIdWithInputDiagnosisOpen,
    clearSlideIdWithInputDiagnosisOpen,
  ] = useLocalStorage<number | undefined>(
    "SLIDE_ID_WITH_INPUT_DIAGNOSIS_OPEN",
    undefined,
    {
      raw: false,
      serializer: (value: number | undefined) => `${value || ""}`,
      deserializer: (value: string) =>
        value ? parseInt(value, 10) : undefined,
    }
  );

  const document = caseDetailObject?.documents.find((d) => d.id === documentId);
  const specimen = caseDetailObject?.specimens?.find(
    (specimen) =>
      specimen.id === slideDetailObject.specimen?.id ||
      specimen.case_documents?.some((doc) => doc.id === documentId) ||
      specimen.id === specimenId
  );

  const viewableSlideStatuses = useViewableSlideStatuses();

  const { zoom: parsedHashZoom } = parseHash(window.location.hash);
  const startWithSpecimen = !!features?.dontCloseSpecimenOnZoom
    ? true
    : !slideId ||
      !slideDetailObject.url_path ||
      !viewableSlideStatuses.includes(slideDetailObject.status!) ||
      !parsedHashZoom ||
      (parsedHashZoom && !Number.isNaN(parsedHashZoom) && parsedHashZoom < 1);
  const initialDialog =
    ((slideIdWithInputDiagnosisOpen &&
      slideDetailObject.id &&
      slideIdWithInputDiagnosisOpen === slideDetailObject.id) ||
      document) &&
    (caseDetailObject?.status === Case.status.WORKING ||
      caseDetailObject?.status === Case.status.ACTIVE) &&
    isInputDiagnosis(
      caseDetailObject,
      specimen,
      user,
      features?.signOutInViewer,
      features?.screenPerSpecimen
    )
      ? DialogEnum.InputDiagnosis
      : specimen // Required for the "Specimen" button and the
      ? DialogEnum.Specimen // dialog, which Darren wants as the default.
      : null; // Otherwise we use null (historic value) so that no dialog is open

  const [dialog, setDialog] = useState<DialogEnum | null>(null);

  const prevSpecimenId = usePrevious(slideDetailObject?.specimen?.id);

  useEffect(() => {
    if (
      prevSpecimenId &&
      slideDetailObject.specimen?.id &&
      slideDetailObject.specimen?.id !== prevSpecimenId &&
      shouldShowSpecimenDialog(slideDetailObject.specimen) &&
      startWithSpecimen
    ) {
      setDialog(DialogEnum.Specimen); // Darren
    }
    // The "Next Case" button involves a mount/unmount cycle; the back button doesn't.
    // After the user hits the back button, the pregnant ternary evaluated above
    // and passed to useState() may evaluate to null, but useState() ignores its
    // argument if SlideViewer has already mounted, requiring this additional
    // sanity check to prevent an orphaned dialog with no button:
    if (!slideDetailObject.specimen?.id && dialog === DialogEnum.Specimen) {
      setDialog(null);
    }
  }, [slideDetailObject.specimen?.id, startWithSpecimen]);

  useEffect(() => {
    if (specimen?.id !== prevSpecimenId && initialDialog && startWithSpecimen) {
      setDialog(initialDialog);
    }
  }, [initialDialog, specimen?.id, prevSpecimenId, startWithSpecimen]);

  // maintain SLIDE_ID_WITH_INPUT_DIAGNOSIS_OPEN value here based on slide id and dialog
  useEffect(() => {
    const slide_id = slideDetailObject.id;
    if (slide_id) {
      if (slideIdWithInputDiagnosisOpen) {
        if (
          slideIdWithInputDiagnosisOpen !== slide_id ||
          dialog !== DialogEnum.InputDiagnosis
        ) {
          // we changed slides, or dialog has been set to something else, so clear this
          clearSlideIdWithInputDiagnosisOpen();
        }
      }
      if (dialog === DialogEnum.InputDiagnosis) {
        // Set it for when we change routes, get amnesia, then come back here
        setSlideIdWithInputDiagnosisOpen(slide_id);
      }
    }
  }, [slideDetailObject.id, dialog]);

  const [patientModal, setPatientModal] = useState(false);
  const [workflowModal, setWorkflowModal] = useState(false);
  const [preferencesModal, setPreferencesModal] = useState(false);
  const [sideWindowOpen, setSideWindowOpen] = useState(false);
  const [manuallyClosedDialog, setManuallyClosedDialog] = useState(false);

  // We only need to load "all" diagnosis to fetch AI-sourced
  // ... when the user has the prospectiveAI flag on
  useEffect(() => {
    if (caseDetailObject?.id && features?.prospectiveAI) {
      dispatch(
        fetchCaseDiagnoses(caseDetailObject.id, caseDetailObject.status, {
          ai_only: true,
        })
      );
    }
  }, [caseDetailObject?.id, features?.prospectiveAI]);

  useSiteSpecificCodes({
    fetchCodes: !!(
      caseDetailObject &&
      canSignoutCase(
        caseDetailObject,
        authObject.user,
        features?.signOutInViewer
      )
    ),
  });

  const slideHeaderProps = {
    slideDetailObject,
    setPatientModal,
    setWorkflowModal,
    setPreferencesModal,
    dialog,
    setDialog,
    specimen: specimen as Specimen,
  };

  const isViewingNonSuccess =
    slideDetailObject.status &&
    viewableSlideStatuses.includes(slideDetailObject.status) &&
    slideDetailObject.status !== Slide.status.SUCCESS;

  const showPrelimBanner =
    !(
      caseDetailObject?.status === Case.status.FINALIZED ||
      caseDetailObject?.status === Case.status.ARCHIVED ||
      caseDetailObject?.status === Case.status.ERROR
    ) &&
    sourceQuery === "preliminary" &&
    caseDetailObject?.prelim_report_id;

  const viewerItems = useViewerItems();

  useHotkeys(
    "y",
    () => setPatientModal((p) => !p),
    { enabled: features.kwonKeys, scopes: ["viewerHotkeys"] },
    []
  );
  useHotkeys(
    "l",
    () => setSideWindowOpen((s) => !s),
    { enabled: features.kwonKeys, scopes: ["viewerHotkeys"] },
    []
  );

  return (
    <div
      className={classNames("SlideViewer", {
        "SlideViewer--impersonating": hasImpersonator,
        "SlideViewer--previewing": isViewingNonSuccess,
        "SlideViewer--preliminaryWarning": showPrelimBanner,
      })}
    >
      {isViewingNonSuccess && (
        <header className="preview-banner alert-warning">
          <div>
            You are viewing a Slide with status = {slideDetailObject.status}.
            This Slide is not viewable for Users without the{" "}
            <code>canViewNonSuccessSlides</code> feature flag.
          </div>
        </header>
      )}
      {showPrelimBanner && (
        <header className="preview-banner alert-warning">
          <div>
            This case has a preliminary diagnosis.{" "}
            <LinkButton
              title="View Preliminary Report"
              className="nav__control nav__button"
              onClick={() => {
                dispatch(
                  openDocument(
                    caseDetailObject?.id || 0,
                    caseDetailObject?.prelim_report_id || 0
                  )
                );
              }}
            >
              Preliminary Report
            </LinkButton>
          </div>
        </header>
      )}
      {caseDetailObject?.id ? (
        <SlideHeader
          {...slideHeaderProps}
          caseDetailObject={caseDetailObject}
        />
      ) : (
        <SlideHeaderCaseless {...slideHeaderProps} />
      )}
      <main>
        {caseDetailObject && (
          <SlideButtons
            caseDetailObject={caseDetailObject}
            slideDetailObject={slideDetailObject}
            document={document}
            authObject={authObject}
            dialog={dialog}
            setDialog={(dialogToSet: DialogEnum) => {
              if (dialogToSet === null) {
                setManuallyClosedDialog(true);
              } else {
                setManuallyClosedDialog(false);
              }

              setDialog(dialogToSet);
            }}
            archived={archived}
            sideWindowOpen={sideWindowOpen}
            setSideWindowOpen={setSideWindowOpen}
            specimenId={specimenId}
          />
        )}
        {((isAvifSlide && avifSupported) || !isAvifSlide) &&
          !features?.mrmcStudy &&
          caseDetailObject?.id &&
          viewerItems.length > 0 && (
            <SlideThumbnailWidget
              highlightedSlideId={slideDetailObject.id}
              highlightedDocumentId={documentId}
              selectedSpecimenId={specimenId}
              canFlag={features?.stainOrdering || canSeeAdmin}
            />
          )}
        {sideWindowOpen && (
          <SideWindow
            amended={amended}
            caseDetail={caseDetailObject?.id ? caseDetailObject : undefined}
            slideDetail={slideDetailObject.id ? slideDetailObject : undefined}
            features={features}
            hasCaseLog={caseLogExists}
            hasSlideAnnotations={slideAnnotationsExist}
            setSideWindowOpen={setSideWindowOpen}
          />
        )}
        {(features?.stainOrdering || canSeeAdmin) &&
          slideDetailObject.flags &&
          slideDetailObject.flags.length !== 0 && (
            <FlagList
              flags={slideDetailObject.flags}
              labOrders={
                caseDetailObject?.lab_orders ||
                slideDetailObject.lab_orders ||
                []
              }
            />
          )}
        <SpecimenDialogController
          dialog={dialog}
          specimen={specimen as Specimen}
          caseDetail={caseDetailObject}
          slideDetailObject={slideDetailObject}
        />
        {dialog === DialogEnum.InputDiagnosis && caseDetailObject && (
          <DiagnosisDialogController
            caseDetailObject={caseDetailObject}
            slideDetailObject={slideDetailObject}
            dialog={dialog}
            setDialog={setDialog}
            aiObject={aiObject}
            specimen={specimen}
          />
        )}
        {dialog === DialogEnum.AIOverlay &&
          classificationInfo !== undefined &&
          aiObject && (
            <AIOverlayDialog
              slideDetailObject={slideDetailObject}
              aiObject={aiObject}
            />
          )}
        {dialog === DialogEnum.StainOrdering && caseDetailObject && (
          <StainOrderDialog
            slideDetailObject={slideDetailObject}
            setDialog={setDialog}
          />
        )}
      </main>
      {!!slideDetailObject.id &&
      !viewableSlideStatuses.includes(
        slideDetailObject.status || Slide.status.ERROR
      ) ? (
        <AppWarning>
          {slideDetailObject.status === Slide.status.RECEIVED_LAB
            ? "Slide has been cut and is pending scan"
            : slideDetailObject.status === Slide.status.PENDING_AI
            ? features.prospectiveAI
              ? "Slide has been cut and is pending AI"
              : "Slide has been cut and is pending scan"
            : `Slide status = ${slideDetailObject.status}`}
        </AppWarning>
      ) : isAvifSlide && !avifSupported ? (
        <AppWarning>
          <span>
            This browser doesn't support the AVIF image format. To view this
            slide, please update your browser or try using the latest version of
            Chrome or Edge.
          </span>
        </AppWarning>
      ) : !!slideDetailObject.id ? (
        <SlideComponent
          slideDetailObject={slideDetailObject}
          emptyCase={
            !slideDetailObject.id && caseDetailObject?.slides.length === 0
          }
          archived={archived}
          setDialog={setDialog}
          manuallyClosedDialog={manuallyClosedDialog}
          dialog={dialog}
        />
      ) : !!document && caseDetailObject ? (
        <div className="OrderedDocument">
          <h5>{document.category}:</h5>
          <OldItem caseDetail={caseDetailObject} document={document} />
        </div>
      ) : !!caseDetailObject &&
        (caseDetailObject.specimens?.length || 0) < 1 ? (
        <AppWarning>
          <span>
            Case <strong>{caseDetailObject.accession}</strong> has no Specimens,
            Slides, or Documents
          </span>
        </AppWarning>
      ) : !!caseDetailObject && (caseDetailObject.slides.length || 0) < 1 ? (
        <AppWarning>
          <span>
            Case <strong>{caseDetailObject.accession}</strong> has no Slides or
            Documents
          </span>
        </AppWarning>
      ) : !!caseDetailObject && !!specimen ? (
        <AppWarning>
          <span>
            Specimen <strong>{specimen.name}</strong> has no Slides or Documents
          </span>
        </AppWarning>
      ) : (
        <AppSpinner />
      )}
      {caseDetailObject && (
        <>
          <PatientModal
            isOpen={patientModal}
            caseDetailObject={caseDetailObject}
            setPatientModal={setPatientModal}
          />
          {features?.caseReassignment && (
            <WorkflowModal
              isOpen={workflowModal}
              caseDetailObject={caseDetailObject}
              setWorkflowModal={setWorkflowModal}
            />
          )}
          {(user?.user_type === "dermpath" || user?.is_superuser) && (
            <PhysicianPreferencesModal
              caseDetailObject={caseDetailObject}
              isOpen={preferencesModal}
              setPreferencesModal={setPreferencesModal}
            />
          )}
        </>
      )}
      <HelpDialog
        dialog={dialog}
        setDialog={setDialog}
        features={features}
        user={user}
      />
      <AnnotationModal
        slideDetailObject={slideDetailObject}
        authObject={authObject}
      />
      <ErrorModal />
    </div>
  );
};

export default SlideViewer;
